import { buildProductSpecRows } from '@/experiences/sales-portal/domain/items/blocs/transformers/product-spec-rows-transformer';
import { buildRelatedVariantPacks } from '@/experiences/sales-portal/domain/items/blocs/transformers/related-variant-packs-transformer';
import { DisplayConfigCategory } from '@/types';
import { getTranslationValue } from '@/utils/translations';
import { inArray, useLiveQuery } from '@platform/db/tanstack-db';
import {
  ItemDetailContentData,
  ItemDetailDisplayConfig,
  ItemDetailItemInfo,
  PriceEditSettings,
  PricingSummary,
  SaltInfo,
  VariantPackInfo,
} from '@/experiences/sales-portal/domain/items/blocs/types/item-detail-content';
import {
  mapPurchaseHistoryEntry,
  mapSaltInfo,
} from '@/experiences/sales-portal/domain/items/blocs/transformers/item-detail-content-transformer';
import {
  orderEntryCollection,
  orderGuideItemCollection,
} from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/collections/order-entry-collection';
import { useCallback, useMemo } from 'react';
import { useCatalogSearchStore } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/stores/catalog-search-store';
import { useGeneratedGetOrderEntryItemDetailQuery } from '@/experiences/sales-portal/domain/items/blocs/documents/item-detail-content.generated';
import type {
  GetOrderEntryItemDetailQuery,
  OrderEntryPriceEditDisplayHeader,
} from '@/types';
import type { OrderEntryTableRowData } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/order-entry-table-transformers';
import type { ProductSpecRow } from '@/experiences/sales-portal/domain/items/blocs/transformers/product-spec-rows-transformer';
import type { RelatedVariantPack } from '@/experiences/sales-portal/domain/items/blocs/transformers/related-variant-packs-transformer';

interface UseItemDetailContentLoaderParams {
  deliveryDate: string | null;
  onDetailLoaded?: (siblingVariantPackIds: string[]) => void;
  priceEditDisplayHeaders?: OrderEntryPriceEditDisplayHeader[];
  purchaseHistoryLimit?: number;
  restaurantId: string;
  restaurantTimezone: string;
  supplierId: string;
  variantPackId: string;
}

const EMPTY_ITEM_INFO: ItemDetailItemInfo = {
  description: '',
  externalItemId: null,
  imageUrl: null,
  itemUUID: null,
  metadata: null,
  name: '',
  packDisplayString: '',
  upcList: [],
};

const mapItemInfo = (
  tableRow: OrderEntryTableRowData | null,
): ItemDetailItemInfo => {
  if (!tableRow) {
    return EMPTY_ITEM_INFO;
  }
  return {
    description: tableRow.description,
    externalItemId: tableRow.externalItemId || null,
    imageUrl: tableRow.imageUrl ?? null,
    itemUUID: null,
    metadata: null,
    name: tableRow.name,
    packDisplayString: tableRow.packDisplayString,
    upcList: [],
  };
};

/**
 * Loader that fetches all data the item detail panel needs. Combines:
 *
 * 1. The order entry table's locally cached variant-pack fragment
 *    (`orderEntryCollection`) — provides item identity (name, image, pack,
 *    SKU). The table is the source of truth; the panel does not re-fetch
 *    these fields. Variant-pack pricing and price-edit gating from this
 *    fragment will be layered in by a follow-up.
 * 2. The `getOrderEntryItemDetail` Hasura action — provides purchase history,
 *    related variant packs, and SALT metadata.
 *
 * On every successful detail fetch, calls `onDetailLoaded` with the related
 * variant pack ids so the consumer can background-prefetch their rows (the
 * panel drilling into a sibling absent from the collections still populates).
 *
 * Skipped while `variantPackId` is empty (panel closed) or `deliveryDate` is
 * null (the action requires a fulfillment date). Refetches when `variantPackId`
 * changes (drill-down).
 *
 * @param params - Loader inputs.
 * @param params.deliveryDate - Selected fulfillment date; null skips the query.
 * @param params.onDetailLoaded - Called on successful fetch with the related
 *   variant pack ids (consumer drives the background prefetch).
 * @param params.priceEditDisplayHeaders - Order-guide-level display-config
 *   headers used to bucket and translate `displayConfigs`.
 * @param params.purchaseHistoryLimit - Optional cap on history entries.
 * @param params.restaurantId - The restaurant the panel is opened from.
 * @param params.restaurantTimezone - IANA timezone used to render purchase-history dates.
 * @param params.supplierId - The supplier the panel is opened from.
 * @param params.variantPackId - The currently displayed variant pack
 *   (top of the drill-down stack).
 * @returns The combined `ItemDetailContentData` plus loading / error / refetch.
 */

export interface OrderEntryItemDetailContentData extends ItemDetailContentData {
  row: OrderEntryTableRowData | null;
}

export const useItemDetailContentLoader = ({
  deliveryDate,
  onDetailLoaded,
  priceEditDisplayHeaders,
  purchaseHistoryLimit,
  restaurantId,
  restaurantTimezone,
  supplierId,
  variantPackId,
}: UseItemDetailContentLoaderParams): OrderEntryItemDetailContentData => {
  const skip = !variantPackId || !deliveryDate;

  const handleDetailCompleted = useCallback(
    (queryData: GetOrderEntryItemDetailQuery): void => {
      const siblingIds = (
        queryData?.getOrderEntryItemDetail?.related_variant_packs ?? []
      ).map((pack) => pack.variant_pack_id);
      onDetailLoaded?.(siblingIds);
    },
    [onDetailLoaded],
  );

  const { data, error, loading, refetch } =
    useGeneratedGetOrderEntryItemDetailQuery({
      notifyOnNetworkStatusChange: true,
      onCompleted: handleDetailCompleted,
      skip,
      variables: {
        deliveryDate: deliveryDate ?? '',
        purchaseHistoryLimit: purchaseHistoryLimit ?? null,
        restaurantId,
        supplierId,
        variantPackId,
      },
    });

  const { data: tableRows } = useLiveQuery(
    (q) => {
      if (!variantPackId) {
        return null;
      }
      return q
        .from({ orderEntryCollection })
        .where(({ orderEntryCollection: row }) =>
          inArray(row.variantPackUUID, [variantPackId]),
        );
    },
    [variantPackId],
  );

  const tableRow = tableRows?.[0] ?? null;

  const catalogResults = useCatalogSearchStore((state) => state.results);
  const catalogRow = useMemo(() => {
    if (!variantPackId) {
      return null;
    }
    return (
      catalogResults.find((row) => row.variantPackUUID === variantPackId) ??
      null
    );
  }, [catalogResults, variantPackId]);

  const { data: rawItems } = useLiveQuery(
    (q) => {
      if (!variantPackId) {
        return null;
      }
      return q
        .from({ orderGuideItemCollection })
        .where(({ orderGuideItemCollection: row }) =>
          inArray(row.variant_pack_id, [variantPackId]),
        );
    },
    [variantPackId],
  );

  const rawItem = rawItems?.[0] ?? null;

  const itemInfo = useMemo(
    () => mapItemInfo(tableRow ?? catalogRow),
    [tableRow, catalogRow],
  );

  const purchaseHistory = useMemo(
    () =>
      (data?.getOrderEntryItemDetail?.purchase_history ?? []).map((entry) =>
        mapPurchaseHistoryEntry(entry, restaurantTimezone),
      ),
    [data, restaurantTimezone],
  );

  const relatedVariantPacks = useMemo<RelatedVariantPack[]>(
    () =>
      buildRelatedVariantPacks(
        data?.getOrderEntryItemDetail?.related_variant_packs ?? [],
      ),
    [data],
  );

  const saltInfo = useMemo<SaltInfo | null>(
    () => mapSaltInfo(data?.getOrderEntryItemDetail?.salt_info),
    [data],
  );

  const productSpecRows = useMemo<ProductSpecRow[]>(
    () => buildProductSpecRows(saltInfo, itemInfo),
    [saltInfo, itemInfo],
  );

  const { displayConfigs, displayTitleTranslations } = useMemo<{
    displayConfigs: ItemDetailDisplayConfig[];
    displayTitleTranslations: Record<string, string>;
  }>(() => {
    const entries = rawItem?.price_edit_display_info ?? [];
    if (entries.length === 0) {
      return { displayConfigs: [], displayTitleTranslations: {} };
    }

    const headerByKey = new Map(
      (priceEditDisplayHeaders ?? []).map((header) => [header.key, header]),
    );

    const titles: Record<string, string> = {};
    const configs: ItemDetailDisplayConfig[] = entries.map((entry) => {
      const header = headerByKey.get(entry.key);
      const title = header
        ? getTranslationValue(header.translation)
        : entry.key;
      titles[entry.key] = title;
      // Scoville emits this field in lowercase via dynamic config, so
      // normalize to uppercase to match the DisplayConfigCategory enum.
      const category = header?.category
        ? (header.category.toUpperCase() as DisplayConfigCategory)
        : null;
      return {
        category,
        currency: entry.currency ?? null,
        key: entry.key,
        title,
        trend: entry.trend ?? null,
        value: entry.value ?? null,
      };
    });

    return { displayConfigs: configs, displayTitleTranslations: titles };
  }, [rawItem, priceEditDisplayHeaders]);

  const variantPackInfo: VariantPackInfo | null = null;
  const priceEditSettings: PriceEditSettings | null = null;

  const pricingSummary = useMemo<PricingSummary>(
    () => ({
      gpDisplay: tableRow?.grossProfitPercentageDisplayString ?? '',
      priceDisplay: tableRow?.priceDisplayString ?? '',
      unitPriceDisplay: tableRow?.unitPriceInfo ?? '',
    }),
    [
      tableRow?.grossProfitPercentageDisplayString,
      tableRow?.priceDisplayString,
      tableRow?.unitPriceInfo,
    ],
  );

  return {
    displayConfigs,
    displayTitleTranslations,
    error: error ?? null,
    itemInfo,
    loading,
    priceEditSettings,
    pricingSummary,
    productSpecRows,
    purchaseHistory,
    refetch,
    relatedVariantPacks,
    row: tableRow ?? catalogRow,
    saltInfo,
    variantPackInfo,
  };
};
